import React, { useState, useEffect, useCallback, useRef } from "react";
import usePersistedState from '../../hooks/usePersistedState';
import useDebounce from '../../hooks/useDebounce';
import Paginate from '../../components/Paginate';
import { Link } from 'react-router-dom';
import { FaSortUp, FaSortDown } from 'react-icons/fa';
import useInvoiceService from '../../hooks/useInvoiceService';
import useClientService from '../../hooks/useClientService';
import useBuildingService from '../../hooks/useBuildingService';
import { useSnackbar } from '../../components/SnackbarProvider';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Autocomplete,
    MenuItem,
    Select,
    TextField,
    Button,
    Box,
    Grid,
    CircularProgress,
    Typography,
    Dialog,
    Card,
    CardContent,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from "@mui/material";
import CreatePdf from '../../components/CreatePdf';
import dayjs from 'dayjs';
import "dayjs/locale/es";
import utc from 'dayjs/plugin/utc';
import {
    formatDate,
    itemsPerPage,
    DecimalTextField,
    formatPrice,
    formatAutocompleteOption,
    DatePickerComponent,
    handleApiError,
    IconBtn
} from '../../components/Utils';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';

dayjs.extend(utc);

function ListClientInvoices() {
    const { getInvoices, payInvoice, disableInvoice } = useInvoiceService();
    const { getClients } = useClientService();
    const { getBuildings } = useBuildingService();
    const showSnackbar = useSnackbar();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchReset, setFetchReset] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(null);

    const [invoices, setInvoices] = useState([]);
    const [amounts, setAmounts] = useState({}); // To store amounts to pay for each invoice
    const [errors, setErrors] = useState({}); // To store errors for each invoice

    const [status, setStatus] = useState(null);
    const [code, setCode] = usePersistedState('list_client_invoices_code', null);
    const [startInvoiceDate, setStartInvoiceDate] = useState(null);
    const [endInvoiceDate, setEndInvoiceDate] = useState(null);

    const debouncedCode = useDebounce(code);

    const [sortColumn, setSortColumn] = useState('');
    const [sortDirection, setSortDirection] = useState('ASC');

    const isFirstFetch = useRef(true);

    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);

    const [buildings, setBuildings] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState(null);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [invoiceToDelete, setInvoiceToDelete] = useState(null);

    const [totalPriceSum, setTotalPriceSum] = useState(null);
    const [paidAmountSum, setPaidAmountSum] = useState(null);
    const [remainingAmountSum, setRemainingAmountSum] = useState(null);
    const [totalOverdue, setTotalOverdue] = useState(null);

    const fetchInvoices = useCallback(async () => {
        try {
            if (isFirstFetch.current) setLoading(true);
            setError(null);
            const params = {};
            params.limit = itemsPerPage;
            params.offset = (currentPage - 1) * itemsPerPage;
            params.only_client_invoices = true;
            if (!fetchReset) {
                if (code) params.code = code;
                if (status) params.invoice_status = status;
                if (selectedBuilding) params.building_id = selectedBuilding.id;
                if (selectedClient) params.client_id = selectedClient.id;
                if (sortColumn) params.sort_by = sortColumn;
                if (sortDirection) params.sort_direction = sortDirection;
                if (startInvoiceDate) {
                    const startDateMidday = dayjs(startInvoiceDate).hour(12).minute(0).second(0);
                    params.start_invoice_date = startDateMidday.utc().format('YYYY-MM-DD');
                }
                if (endInvoiceDate) {
                    const endDateMidday = dayjs(endInvoiceDate).hour(12).minute(0).second(0);
                    params.end_invoice_date = endDateMidday.utc().format('YYYY-MM-DD');
                }
            } else {
                setFetchReset(false);
            }

            const result = await getInvoices(params);
            const data = result.data;
            setInvoices(data.items);
            setTotalItems(data.total);
            setTotalPriceSum(data.total_price_sum);
            setPaidAmountSum(data.paid_amount_sum);
            setRemainingAmountSum(data.remaining_amount_sum);
            setTotalOverdue(data.total_invoice_overdue);
            isFirstFetch.current = false;
        } catch (error) {
            setError(handleApiError(error, 'Error al cargar las facturas.'));
        } finally {
            setLoading(false);
        }
    }, [currentPage, fetchReset, startInvoiceDate, endInvoiceDate, code, status, selectedClient, selectedBuilding, sortColumn, sortDirection]);

    const fetchClients = useCallback(async () => {
        try {
            setLoading(true);

            const result = await getClients({});

            setClients(result.data.items);
            setLoading(false);
        } catch (error) {
            setError(handleApiError(error, 'Error al cargar los clientes.'));
        }
    }, []);

    const fetchBuildings = useCallback(async () => {
        try {
            setLoading(true);

            const result = await getBuildings({});

            setBuildings(result.data.items);
            setLoading(false);
        } catch (error) {
            setError(handleApiError(error, 'Error al cargar las obras.'));
        }
    }, []);

    useEffect(() => {
        fetchClients();
        fetchBuildings();
    }, []);

    useEffect(() => {
        fetchInvoices();
    }, [currentPage, sortColumn, sortDirection, fetchReset, debouncedCode, status, selectedClient, selectedBuilding, startInvoiceDate, endInvoiceDate]);

    const handleStatusChange = (event) => {
        const { value } = event.target;
        setStatus(value);
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(column);
            setSortDirection('ASC');
        }
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setCurrentPage(1);
        setStatus(null);
        setSelectedClient(null);
        setSelectedBuilding(null);
        setCode(null);
        setStartInvoiceDate(null);
        setEndInvoiceDate(null);
        setFetchReset(true);
    };

    const handleDateChange = (newValue, name) => {
        if (name === 'start') {
            setStartInvoiceDate(newValue);
        } else if (name === 'end') {
            setEndInvoiceDate(newValue);
        }
    };

    const handleClientChange = (event, newValue) => {
        setSelectedClient(newValue);
    };

    const handleBuildingChange = (event, newValue) => {
        setSelectedBuilding(newValue);
    };

    const handleAmountChange = (id, value) => {
        const invoice = invoices.find(inv => inv.id === id);
        const remainingAmount = invoice.total_price - (invoice.paid_amount ?? 0) - (invoice.retention ?? 0);

        let newErrors = { ...errors };

        // Validate the amount
        if (value <= 0) {
            newErrors[id] = "El monto debe ser mayor a 0.";
        } else if (value > remainingAmount) {
            newErrors[id] = `El monto no puede exceder el restante (${remainingAmount.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 })}€).`;
        } else {
            delete newErrors[id];
        }

        setAmounts({
            ...amounts,
            [id]: value
        });

        setErrors(newErrors);
    };
    const handlePayInvoice = async (id) => {
        try {
            const amount = amounts[id];
            if (!amount || amount <= 0) {
                showSnackbar("Por favor, introduzca un valor válido.", "error");
                return;
            }
            await payInvoice({ invoice_id: id, amount: amount });
            showSnackbar('Factura pagada correctamente', 'success');
            fetchInvoices();
            setError(null);
        } catch (error) {
            const msg = handleApiError(error, 'Error al pagar la factura.');
            setError(msg);
            showSnackbar(msg, 'error');
        }
    };

    const handleOpenDeleteDialog = (id) => {
        setInvoiceToDelete(id);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setInvoiceToDelete(null);
    };

    const handleDeleteInvoice = async () => {
        try {
            await disableInvoice(invoiceToDelete);
            showSnackbar('Eliminado correctamente', 'success');
            fetchInvoices();
            setOpenDeleteDialog(false);
            setError(null);
        } catch (error) {
            const msg = handleApiError(error, 'Error al eliminar la factura.');
            setError(msg);
            showSnackbar(msg, 'error');
        }
    };

    const littleCellStyle = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '5%'
    };
    const mediumCellStyle = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '6%'
    };
    const bigCellStyle = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '25%'
    };
    return (
        <div>
            {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading && (
                <>
                    {/* Filters and Sorting */}
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={3} sm={2}>
                                    <TextField
                                        fullWidth
                                        label="Código"
                                        name="code"
                                        value={code || ""}
                                        onChange={(e) => setCode(e.target.value)}
                                    />
                                </Grid>
                                <Grid item sm={2}>
                                    <Autocomplete
                                        options={buildings}
                                        getOptionLabel={(option) => formatAutocompleteOption(option)}
                                        value={selectedBuilding}
                                        onChange={handleBuildingChange}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Obra"
                                                style={{ maxWidth: '100%' }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item sm={2}>
                                    <Autocomplete
                                        options={clients}
                                        getOptionLabel={(option) => formatAutocompleteOption(option)}
                                        value={selectedClient}
                                        onChange={handleClientChange}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Cliente"
                                                style={{ maxWidth: '100%' }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3} sm={2}>
                                    <FormControl fullWidth>
                                        <InputLabel id="status-label">Estado</InputLabel>
                                        <Select
                                            labelId="status-label"
                                            id="status"
                                            name="status"
                                            value={status}
                                            onChange={handleStatusChange}
                                            label="Tipo"
                                        >
                                            <MenuItem value="paid">PAGADA</MenuItem>
                                            <MenuItem value="not_paid">NO PAGADA</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container spacing={1} alignItems="center">
                                        <Grid item>
                                            <DatePickerComponent
                                                label="Fecha de Inicio"
                                                value={startInvoiceDate}
                                                onChange={(newValue) => handleDateChange(newValue, 'start')}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <DatePickerComponent
                                                label="Fecha de Fin"
                                                value={endInvoiceDate}
                                                onChange={(newValue) => handleDateChange(newValue, 'end')}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container spacing={1} justifyContent="flex-end">
                                <Grid item>
                                    <Button variant="contained" color="secondary" onClick={handleResetFilters}>
                                        Resetear Filtros
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <br />
                    <br />
                    {/* Sums Display */}
                    <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ marginBottom: 3 }}>
                        <Grid item xs={12} sm={3}>
                            <Card elevation={3} sx={{ padding: 1.5 }}>
                                <CardContent>
                                    <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
                                        Total Importe
                                    </Typography>
                                    <Typography variant="h6" align="center" color="primary">
                                        {formatPrice(totalPriceSum)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Card elevation={3} sx={{ padding: 1.5 }}>
                                <CardContent>
                                    <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
                                        Total Pagado
                                    </Typography>
                                    <Typography variant="h6" align="center" color="primary">
                                        {formatPrice(paidAmountSum)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Card elevation={3} sx={{ padding: 1.5 }}>
                                <CardContent>
                                    <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
                                        Total Restante
                                    </Typography>
                                    <Typography variant="h6" align="center" color="primary">
                                        {formatPrice(remainingAmountSum)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Card elevation={3} sx={{ padding: 1.5 }}>
                                <CardContent>
                                    <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
                                        Total Vencido
                                    </Typography>
                                    <Typography variant="h6" align="center" color="primary">
                                        {formatPrice(totalOverdue)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <br />
                    <br />
                    <TableContainer component={Paper}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={littleCellStyle}>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}>
                                                Código {sortColumn === 'code' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={littleCellStyle}>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('invoice_date')} style={{ cursor: 'pointer' }}>
                                                Fecha {sortColumn === 'invoice_date' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={littleCellStyle}>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('invoice_limit_date')} style={{ cursor: 'pointer' }}>
                                                Fecha vencimiento {sortColumn === 'invoice_limit_date' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={littleCellStyle}>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('building_id')} style={{ cursor: 'pointer' }}>
                                                Obra {sortColumn === 'building_id' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={littleCellStyle}>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('client_id')} style={{ cursor: 'pointer' }}>
                                                Cliente {sortColumn === 'client_id' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={littleCellStyle}>
                                        <Typography variant="h6" gutterBottom>
                                            Estado
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={mediumCellStyle}>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('total_price')} style={{ cursor: 'pointer' }}>
                                                Importe {sortColumn === 'total_price' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={mediumCellStyle}>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('total_to_pay_without_retention')} style={{ cursor: 'pointer' }}>
                                                Total a pagar {sortColumn === 'total_to_pay_without_retention' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={mediumCellStyle}>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('retention')} style={{ cursor: 'pointer' }}>
                                                Retención {sortColumn === 'retention' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={mediumCellStyle}>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('paid_amount')} style={{ cursor: 'pointer' }}>
                                                Pagado {sortColumn === 'paid_amount' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={mediumCellStyle}>
                                        <Typography variant="h6" gutterBottom>
                                            <span onClick={() => handleSort('remaining_to_pay')} style={{ cursor: 'pointer' }}>
                                                Restante {sortColumn === 'remaining_to_pay' && (sortDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />)}
                                            </span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: '10%'
                                    }}>
                                        <Typography variant="h6" gutterBottom>
                                            Cantidad a Pagar
                                        </Typography>
                                    </TableCell>
                                    <TableCell style={bigCellStyle}>
                                        <Typography variant="h6" gutterBottom>
                                            Acciones
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invoices.map((item) => {
                                    const totalToPay = item.total_price - item.retention;
                                    const remainingAmount = totalToPay - item.paid_amount;

                                    return (
                                        <TableRow key={item.id}>
                                            <TableCell component="th" scope="row">
                                                <Link to={`/invoices/update/${item.id}`}>
                                                    {item.code || '-'}
                                                </Link>
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {formatDate(item.invoice_date) || '-'}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {formatDate(item.invoice_limit_date) || '-'}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                <Link to={`/buildings/update/${item.building.id}`}>
                                                    {item.building.code || '-'}
                                                </Link>
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                <Link to={`/clients/update/${item.client.id}`}>
                                                    {item.client.code || '-'}
                                                </Link>
                                            </TableCell>
                                            <TableCell>
                                                {item.invoice_status === 'paid' ? 'PAGADA' :
                                                    item.invoice_status === 'not_paid' ? 'NO PAGADA' :
                                                        item.invoice_status || '-'}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {formatPrice(item.total_price)}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {formatPrice(totalToPay)}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {formatPrice(item.retention)}
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {formatPrice(item.paid_amount)}
                                            </TableCell>
                                            <TableCell>
                                                {formatPrice(remainingAmount)}
                                            </TableCell>

                                            <TableCell>
                                                <DecimalTextField
                                                    type="number"
                                                    value={amounts[item.id] || ''}
                                                    onChange={(e) => handleAmountChange(item.id, e.target.value)}
                                                    fullWidth
                                                    error={!!errors[item.id]}
                                                    helperText={errors[item.id]}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        sx={{ minHeight: 34 }}
                                                        onClick={() => handlePayInvoice(item.id)}
                                                        disabled={remainingAmount <= 0 || !!errors[item.id] || !amounts[item.id]}
                                                    >
                                                        Pagar
                                                    </Button>
                                                    <CreatePdf
                                                        endpoint={`/invoices/${item.id}/generate-pdf/proform`}
                                                        text="Proforma"
                                                        showLabel
                                                    />
                                                    <CreatePdf
                                                        endpoint={`/invoices/${item.id}/generate-pdf/invoice`}
                                                        text="Factura"
                                                        showLabel
                                                    />
                                                    <IconBtn
                                                        icon={<DeleteIcon />}
                                                        title="Eliminar"
                                                        color="error"
                                                        onClick={() => handleOpenDeleteDialog(item.id)}
                                                    />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )
            }
            <br />
            <Paginate
                totalItems={totalItems}
                onPageChange={setCurrentPage}
                currentPage={currentPage}
            />
            {
                error && (
                    <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                        {error}
                    </Typography>
                )
            }
            <Dialog
                open={openDeleteDialog}
                onClose={handleCloseDeleteDialog}
            >
                <DialogTitle>Confirmar Borrado</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Estás seguro de que quieres borrar esta factura?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} color="primary">
                        Cancelar
                    </Button>
                    <Button onClick={handleDeleteInvoice} color="secondary" autoFocus>
                        Borrar
                    </Button>
                </DialogActions>
            </Dialog>
        </div >
    );
}

export default ListClientInvoices;
